import type {
  PuppyAssemblyDocument,
  PuppyAssemblyNode,
} from './types'
import { remapPuppyAssemblyMateDocumentNodeReferences } from './remapPuppyAssemblyMateDocument'
import {
  readPuppyDatumExtensionFromAssemblyExtensions,
  remapPuppyDatumExtensionNodeReferences,
  writePuppyDatumExtensionToAssemblyExtensions,
} from './puppyDatumExtension'
//------ 线性零部件阵列离线包定义持久化 开始------
import {
  readPuppyLinearComponentPatternExtensionFromAssemblyExtensions,
  remapPuppyLinearComponentPatternNodeReferences,
  writePuppyLinearComponentPatternExtensionToAssemblyExtensions,
} from './puppyLinearComponentPatternExtension'
//------ 线性零部件阵列离线包定义持久化 结束------

/**
 * Puppy 装配刚体层级归一化结果。
 */
export interface PuppyAssemblyRigidHierarchyNormalizationResult {
  /** 归一化后的 assembly.json 文档。 */
  readonly assembly: PuppyAssemblyDocument
  /** 被折叠节点 ID 到保留节点 ID 的映射。 */
  readonly nodeIdAliasByNodeId: Readonly<Record<string, string>>
}

/**
 * 深拷贝可序列化数据。
 *
 * @param value 源数据。
 * @returns 深拷贝后的数据。
 */
function cloneSerializable<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T
}

/**
 * 判断节点是否表达零件刚体。
 *
 * @param node 装配节点。
 * @returns 是否为零件节点。
 */
function isPartNode(node: PuppyAssemblyNode): boolean {
  return node.semanticKind === 'part' || node.meta?.semanticKind === 'part'
}

/**
 * 向数组追加去重字符串。
 *
 * @param target 目标数组。
 * @param values 待追加值。
 */
function appendUniqueStrings(target: string[], values: readonly string[]): void {
  values.forEach((value) => {
    if (value.trim().length > 0 && !target.includes(value)) {
      target.push(value)
    }
  })
}

/**
 * 把显示和拓扑绑定合并到保留的零件节点。
 *
 * @param target 保留的零件节点。
 * @param source 被折叠的显示承载节点。
 */
function mergeDisplayBindingsIntoPartNode(
  target: PuppyAssemblyNode,
  source: PuppyAssemblyNode,
): void {
  appendUniqueStrings(target.displayNodeIds, source.displayNodeIds)
  appendUniqueStrings(target.bodyBindingIds, source.bodyBindingIds)
  appendUniqueStrings(target.geometryObjectIds, source.geometryObjectIds)
}

/**
 * 归一化 `.puppy` 装配刚体层级。
 *
 * 设计说明：
 * SOLIDWORKS 中零件是刚体边界，零件内部的多个 body 不能被还原成可独立移动的子零件。
 * 旧导出链路可能把 `part -> COMPOUND/COMPOUND_1` 这类显示承载层写成子 `part`，
 * 导入后会导致 Mate 求解写入错误 transform root。这里把零件下的所有后代显示绑定折回最近的父零件。
 *
 * @param assembly 原始 assembly.json 文档。
 * @returns 归一化结果。
 * @throws 当装配层级引用不存在的节点时。
 * @throws 当待折回的显示节点找不到已归一化的父零件节点时。
 * @throws 当装配根节点在刚体层级归一化过程中被折叠时。
 */
export function normalizePuppyAssemblyRigidHierarchy(
  assembly: PuppyAssemblyDocument,
): PuppyAssemblyRigidHierarchyNormalizationResult {
  const sourceNodesById = new Map(assembly.nodes.map((node) => [node.nodeId, node]))
  const normalizedNodes: PuppyAssemblyNode[] = []
  const normalizedNodeById = new Map<string, PuppyAssemblyNode>()
  const childIdsByNodeId: Record<string, string[]> = {}
  const nodeIdAliasByNodeId: Record<string, string> = {}

  const visitNode = (
    nodeId: string,
    parentNodeId: string | null,
    nearestPartNodeId: string | null,
  ): string | null => {
    const sourceNode = sourceNodesById.get(nodeId)
    if (!sourceNode) {
      throw new Error(`PuppyCAD assembly.json 引用了未知节点: ${nodeId}`)
    }

    if (nearestPartNodeId) {
      const targetPartNode = normalizedNodeById.get(nearestPartNodeId)
      if (!targetPartNode) {
        throw new Error(`PuppyCAD assembly.json 缺少父零件节点: ${nearestPartNodeId}`)
      }
      mergeDisplayBindingsIntoPartNode(targetPartNode, sourceNode)
      nodeIdAliasByNodeId[sourceNode.nodeId] = nearestPartNodeId
      const childNodeIds = assembly.childIdsByNodeId[sourceNode.nodeId] ?? []
      childNodeIds.forEach((childNodeId) => {
        visitNode(childNodeId, nearestPartNodeId, nearestPartNodeId)
      })
      return null
    }

    const normalizedNode = cloneSerializable(sourceNode)
    normalizedNode.parentNodeId = parentNodeId
    normalizedNodes.push(normalizedNode)
    normalizedNodeById.set(normalizedNode.nodeId, normalizedNode)
    childIdsByNodeId[normalizedNode.nodeId] = []

    const nextNearestPartNodeId = isPartNode(normalizedNode) ? normalizedNode.nodeId : null
    const childNodeIds = assembly.childIdsByNodeId[sourceNode.nodeId] ?? []
    childNodeIds.forEach((childNodeId) => {
      const retainedChildNodeId = visitNode(
        childNodeId,
        normalizedNode.nodeId,
        nextNearestPartNodeId,
      )
      if (retainedChildNodeId) {
        childIdsByNodeId[normalizedNode.nodeId]!.push(retainedChildNodeId)
      }
    })

    return normalizedNode.nodeId
  }

  const rootNodeId = visitNode(assembly.rootNodeId, null, null)
  if (!rootNodeId) {
    throw new Error(`PuppyCAD assembly.json 根节点不能被折叠: ${assembly.rootNodeId}`)
  }
  const datumExtension = readPuppyDatumExtensionFromAssemblyExtensions(assembly.extensions)
  const remappedDatumExtension = datumExtension
    ? remapPuppyDatumExtensionNodeReferences({
      datumExtension,
      aliasByNodeId: nodeIdAliasByNodeId,
    })
    : undefined
  //------ 线性零部件阵列离线包定义持久化 开始------
  const linearComponentPatternExtension =
    readPuppyLinearComponentPatternExtensionFromAssemblyExtensions(assembly.extensions)
  const remappedLinearComponentPatternExtension = linearComponentPatternExtension
    ? remapPuppyLinearComponentPatternNodeReferences({
      document: linearComponentPatternExtension,
      aliasByNodeId: nodeIdAliasByNodeId,
    })
    : undefined
  const normalizedExtensions = writePuppyLinearComponentPatternExtensionToAssemblyExtensions(
    writePuppyDatumExtensionToAssemblyExtensions(
      assembly.extensions,
      remappedDatumExtension,
    ),
    remappedLinearComponentPatternExtension,
  )
  //------ 线性零部件阵列离线包定义持久化 结束------

  return {
    assembly: {
      ...cloneSerializable(assembly),
      rootNodeId,
      nodes: normalizedNodes,
      childIdsByNodeId,
      mates: remapPuppyAssemblyMateDocumentNodeReferences({
        mates: assembly.mates,
        aliasByNodeId: nodeIdAliasByNodeId,
      }),
      extensions: normalizedExtensions,
    },
    nodeIdAliasByNodeId,
  }
}
